// Graphics levels and the Auto mode (ui-flow §4.5, architecture §9.1 step 8). DOM-free.
//
// - `qualityProfile(level, dpr)` is the ui-flow §4.5 table (plus visuals §3 / backlog R3-5: jelly
//   at Sharp and High only; perfect circles at Low and Very low).
// - `QualityController` resolves the Graphics setting ('auto' or a fixed level) to a level. Auto
//   starts at Sharp when `devicePixelRatio > 1` and the canvas is at most 2560 CSS px wide
//   (otherwise High), drops one level when the FPS stays below 50 for 5 s, and rises one level
//   when it stays above 58 for 30 s, never above where it started. Feed it once per drawn frame
//   with `sample(dtMs, active)`; pass `active = false` while the menu caps the loop at 30 fps.
//
// renderer.ts re-exports QUALITY_LEVELS, QualityLevel, RenderQuality and qualityProfile.

import type { QualitySetting } from '../settings/store.js';

export const QUALITY_LEVELS = ['sharp', 'high', 'medium', 'low', 'verylow'] as const;
export type QualityLevel = (typeof QUALITY_LEVELS)[number];

/** Everything the renderer needs to know about the active graphics level. */
export interface RenderQuality {
  readonly level: QualityLevel;
  /** Canvas backing scale (backing size = CSS size × scale). */
  readonly scale: number;
  /** Sketch style: outline boil animates (else a static wobble). */
  readonly boil: boolean;
  /** Perfect circles only (no wobble, no spikes detail beyond the basic virus): Low, Very low. */
  readonly perfectCircles: boolean;
  /** Classic jelly soft-body outlines (visuals §4; backlog R3-5: off at Medium and below). */
  readonly jelly: boolean;
  /** Food drawn from sprites (else plain arcs). */
  readonly foodSprites: boolean;
  /** Skins only when the on-screen radius is at least this (px). */
  readonly minSkinRadiusPx: number;
  /** Names only when the on-screen radius is at least this (px). */
  readonly minNameRadiusPx: number;
  /** Draw the 50 u minor grid. */
  readonly minorGrid: boolean;
}

/** The ui-flow §4.5 table. `devicePixelRatio` feeds Sharp's scale `min(dpr, 2)`. */
export function qualityProfile(level: QualityLevel, devicePixelRatio = 1): RenderQuality {
  switch (level) {
    case 'sharp':
      return {
        level,
        scale: Math.min(Math.max(devicePixelRatio, 1), 2),
        boil: true,
        perfectCircles: false,
        jelly: true,
        foodSprites: true,
        minSkinRadiusPx: 0,
        minNameRadiusPx: 0,
        minorGrid: true,
      };
    case 'high':
      return {
        level,
        scale: 1,
        boil: true,
        perfectCircles: false,
        jelly: true,
        foodSprites: true,
        minSkinRadiusPx: 0,
        minNameRadiusPx: 0,
        minorGrid: true,
      };
    case 'medium':
      return {
        level,
        scale: 0.9,
        boil: false,
        perfectCircles: false,
        jelly: false,
        foodSprites: true,
        minSkinRadiusPx: 0,
        minNameRadiusPx: 0,
        minorGrid: true,
      };
    case 'low':
      return {
        level,
        scale: 0.75,
        boil: false,
        perfectCircles: true,
        jelly: false,
        foodSprites: false,
        minSkinRadiusPx: 16,
        minNameRadiusPx: 0,
        minorGrid: true,
      };
    case 'verylow':
      return {
        level,
        scale: 0.5,
        boil: false,
        perfectCircles: true,
        jelly: false,
        foodSprites: false,
        minSkinRadiusPx: 16,
        minNameRadiusPx: 30,
        minorGrid: false,
      };
  }
}

/** Auto graphics thresholds (ui-flow §4.5). */
export const AUTO_QUALITY = Object.freeze({
  /** Start at Sharp only on canvases up to this many CSS px wide. */
  sharpMaxCssWidth: 2560,
  dropBelowFps: 50,
  dropAfterSec: 5,
  riseAboveFps: 58,
  riseAfterSec: 30,
  /** A frame gap longer than this (a hidden tab, a debugger pause) restarts the measurement. */
  resetGapMs: 1000,
});

/** Auto's starting level: Sharp on high-DPI screens up to 2560 CSS px wide, else High. */
export function autoStartLevel(devicePixelRatio: number, cssWidth: number): QualityLevel {
  return devicePixelRatio > 1 && cssWidth <= AUTO_QUALITY.sharpMaxCssWidth ? 'sharp' : 'high';
}

function levelIndex(level: QualityLevel): number {
  return QUALITY_LEVELS.indexOf(level);
}

function levelAt(index: number): QualityLevel {
  return QUALITY_LEVELS[Math.max(0, Math.min(QUALITY_LEVELS.length - 1, index))] ?? 'high';
}

/**
 * Resolves the Graphics setting to a level and runs Auto mode. The FPS is measured in 1 s
 * buckets: 5 consecutive buckets under 50 fps drop one level; 30 consecutive buckets over 58 fps
 * raise one level (never above the starting level). Each change restarts the measurement.
 */
export class QualityController {
  private settingValue: QualitySetting;
  private dpr: number;
  private cssWidth: number;
  private startLevel: QualityLevel = 'high';
  private current: QualityLevel = 'high';
  private cached: RenderQuality;
  private bucketMs = 0;
  private bucketFrames = 0;
  private slowSeconds = 0;
  private fastSeconds = 0;

  constructor(setting: QualitySetting = 'auto', devicePixelRatio = 1, cssWidth = 1920) {
    this.settingValue = setting;
    this.dpr = devicePixelRatio;
    this.cssWidth = cssWidth;
    this.cached = qualityProfile('high', devicePixelRatio);
    this.resolve();
  }

  get setting(): QualitySetting {
    return this.settingValue;
  }

  /** The active level. */
  get level(): QualityLevel {
    return this.current;
  }

  /** The active level's profile (the same object until the level or the DPR changes). */
  get profile(): RenderQuality {
    return this.cached;
  }

  /** Auto's starting (and highest) level; for a fixed setting, that level. */
  get ceiling(): QualityLevel {
    return this.startLevel;
  }

  /**
   * Applies a new Graphics setting or screen. Returns true when the active level or its profile
   * changed (resize the canvas then).
   */
  configure(
    setting: QualitySetting,
    devicePixelRatio = this.dpr,
    cssWidth = this.cssWidth,
  ): boolean {
    const before = this.cached;
    const sameSetting = setting === this.settingValue;
    if (sameSetting && devicePixelRatio === this.dpr && cssWidth === this.cssWidth) return false;
    this.settingValue = setting;
    this.dpr = devicePixelRatio;
    this.cssWidth = cssWidth;
    if (setting === 'auto' && sameSetting) {
      // Only the screen changed: re-evaluate Auto's ceiling but keep a level Auto already earned.
      this.startLevel = autoStartLevel(devicePixelRatio, cssWidth);
      const keep = levelIndex(this.current) >= levelIndex(this.startLevel);
      this.setLevel(keep ? this.current : this.startLevel);
    } else {
      this.resolve();
    }
    return this.cached.level !== before.level || this.cached.scale !== before.scale;
  }

  /**
   * One drawn frame `dtMs` after the previous one. `active = false` (menu open, 30 fps cap, tab
   * hidden) pauses Auto and discards the partial measurement. Returns true when the level changed.
   */
  sample(dtMs: number, active = true): boolean {
    if (this.settingValue !== 'auto') return false;
    if (!active || !(dtMs > 0) || dtMs > AUTO_QUALITY.resetGapMs) {
      this.resetMeasurement();
      return false;
    }
    this.bucketMs += dtMs;
    this.bucketFrames += 1;
    if (this.bucketMs < 1000) return false;
    const fps = (this.bucketFrames * 1000) / this.bucketMs;
    this.bucketMs = 0;
    this.bucketFrames = 0;
    this.slowSeconds = fps < AUTO_QUALITY.dropBelowFps ? this.slowSeconds + 1 : 0;
    this.fastSeconds = fps > AUTO_QUALITY.riseAboveFps ? this.fastSeconds + 1 : 0;
    const idx = levelIndex(this.current);
    if (this.slowSeconds >= AUTO_QUALITY.dropAfterSec && idx < QUALITY_LEVELS.length - 1) {
      this.setLevel(levelAt(idx + 1));
      return true;
    }
    if (this.fastSeconds >= AUTO_QUALITY.riseAfterSec && idx > levelIndex(this.startLevel)) {
      this.setLevel(levelAt(idx - 1));
      return true;
    }
    return false;
  }

  private resolve(): void {
    if (this.settingValue === 'auto') {
      this.startLevel = autoStartLevel(this.dpr, this.cssWidth);
      this.setLevel(this.startLevel);
    } else {
      this.startLevel = this.settingValue;
      this.setLevel(this.settingValue);
    }
  }

  private setLevel(level: QualityLevel): void {
    this.current = level;
    this.cached = qualityProfile(level, this.dpr);
    this.resetMeasurement();
  }

  private resetMeasurement(): void {
    this.bucketMs = 0;
    this.bucketFrames = 0;
    this.slowSeconds = 0;
    this.fastSeconds = 0;
  }
}
